import React, { useState } from 'react';
import axios from 'axios';

const AddSlideModal = ({ onClose, onAdd }) => {
  const [newItem, setNewItem] = useState({
    desktopImage: null,
    desktopImageFile: null,
    mobileImage: null,
    mobileImageFile: null,
    title: '',
    subtitle: '',
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

  const handleImageUpload = (e, imageType) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (imageType === 'desktop') {
          setNewItem({ 
            ...newItem, 
            desktopImage: reader.result, 
            desktopImageFile: file 
          });
        } else {
          setNewItem({ 
            ...newItem, 
            mobileImage: reader.result, 
            mobileImageFile: file 
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadCarousel = async (formData) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/upload-carousal`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Failed to upload carousel');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!newItem.desktopImageFile || !newItem.mobileImageFile || !newItem.title.trim()) {
      setError('Please fill in all required fields including both images.');
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('title', newItem.title);
      formData.append('subtitle', newItem.subtitle);
      formData.append('desktopImage', newItem.desktopImageFile);
      formData.append('mobileImage', newItem.mobileImageFile);

      const response = await uploadCarousel(formData);

      // Call the parent's onAdd with the response data
      onAdd({
        id: response.carousal.id,
        title: response.carousal.title,
        subtitle: response.carousal.subtitle,
        desktopImage: response.carousal.desktopImage,
        mobileImage: response.carousal.mobileImage,
        isActive: newItem.isActive,
      });

      // Close the modal on success
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Add New Slide</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isLoading}
          >
            &times;
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Desktop Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Desktop Image (1920x1080) *
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, 'desktop')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              disabled={isLoading}
            />
            {newItem.desktopImage && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Desktop Preview:</p>
                <img 
                  src={newItem.desktopImage} 
                  alt="Desktop Preview" 
                  className="w-full h-32 object-cover rounded-lg border" 
                />
              </div>
            )}
          </div>

          {/* Mobile Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Image (1080x1920) *
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, 'mobile')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              disabled={isLoading}
            />
            {newItem.mobileImage && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Mobile Preview:</p>
                <img 
                  src={newItem.mobileImage} 
                  alt="Mobile Preview" 
                  className="w-24 h-32 object-cover rounded-lg border mx-auto" 
                />
              </div>
            )}
          </div>

          {/* Image Previews Side by Side */}
          {(newItem.desktopImage || newItem.mobileImage) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 mb-2">Desktop (1920x1080)</p>
                {newItem.desktopImage ? (
                  <img 
                    src={newItem.desktopImage} 
                    alt="Desktop Preview" 
                    className="w-full h-24 object-cover rounded border" 
                  />
                ) : (
                  <div className="w-full h-24 bg-gray-200 rounded border flex items-center justify-center">
                    <span className="text-gray-400 text-xs">No image selected</span>
                  </div>
                )}
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 mb-2">Mobile (1080x1920)</p>
                {newItem.mobileImage ? (
                  <img 
                    src={newItem.mobileImage} 
                    alt="Mobile Preview" 
                    className="w-16 h-24 object-cover rounded border mx-auto" 
                  />
                ) : (
                  <div className="w-16 h-24 bg-gray-200 rounded border mx-auto flex items-center justify-center">
                    <span className="text-gray-400 text-xs">No image</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Slide title"
              required
              disabled={isLoading}
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subtitle
            </label>
            <input
              type="text"
              value={newItem.subtitle}
              onChange={(e) => setNewItem({ ...newItem, subtitle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Slide subtitle"
              disabled={isLoading}
            />
          </div>

          {/* Active Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={newItem.isActive}
              onChange={(e) => setNewItem({ ...newItem, isActive: e.target.checked })}
              id="active-checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              disabled={isLoading}
            />
            <label htmlFor="active-checkbox" className="ml-2 block text-sm text-gray-700">
              Active
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center justify-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Add Slide'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSlideModal;